import { API_TARGET, AUTH } from '../lib/auth-config';

/**
 * Which backend this dev build is talking to, said out loud and permanently.
 *
 * **The switch is the easy half; this is the half that matters.** Pointing the
 * local app at the deployed stack is genuinely useful — it is the only way to
 * see a change against real photographs, real teams and a real sign-in — and it
 * means every tap on the screen writes to the real thing. A like, a rename, an
 * award, a delete: all of them land. The failure this component exists to
 * prevent is doing that while believing you are on the mock.
 *
 * So it is not dismissible and it does not fade. It is small, fixed, and
 * `pointer-events: none` so it can never swallow a tap on the app underneath.
 * Bottom-left, because the camera FAB owns bottom-right.
 *
 * **It cannot ship.** `import.meta.env.DEV` is a literal `false` in a
 * production build, so this returns before anything renders and the whole
 * component is eliminated — the same property `auth-config` relies on to make
 * the API base unpointable in a real build.
 */
export default function BackendBadge() {
  if (!import.meta.env.DEV) return null;

  const prod = API_TARGET === 'prod';
  return (
    <div
      className={`backend-badge${prod ? ' backend-badge-prod' : ''}`}
      role="status"
      aria-label={prod
        ? 'Development build pointed at the production API. Changes are real.'
        : 'Development build pointed at the local mock API.'}
    >
      <strong>{prod ? 'PROD' : 'MOCK'}</strong>
      {/* The host, because "PROD" is a claim and this is the evidence for it —
          and because when the mock is not running, seeing the URL is what tells
          you that rather than a wall of failed requests. */}
      <span>{hostOf(AUTH.api)}</span>
    </div>
  );
}

const hostOf = (url: string) => {
  try {
    const { hostname, port } = new URL(url);
    return port ? `${hostname}:${port}` : hostname;
  } catch {
    return url;
  }
};
